import React from "react";
import { MdAutoAwesome } from "react-icons/md";

const split = (name) => {
  const [title, ...rest] = name.split(" · ");
  return { title, hint: rest.join(" · ") };
};

// charges of one group, folded into cards by charge name: "Mall Delivery · minimum" and
// "Mall Delivery · per kg" become one "Mall Delivery" card with two values
const toCards = (items) => {
  const cards = [];
  items.forEach((c) => {
    const { title, hint } = split(c.name);
    const last = cards[cards.length - 1];
    if (c.slab) {
      // slab-based charge: one card, one row per slab (From / To / Unit / rate / Min)
      if (!last || !last.slabRows || last.title !== title) {
        cards.push({ title, slabRows: [], items: [c] });
      }
      const card = cards[cards.length - 1];
      let row = card.slabRows.find((r) => r.slab.id === c.slab.id);
      if (!row) {
        row = { slab: c.slab };
        card.slabRows.push(row);
      }
      row[c.slabField] = c;
      return;
    }
    if (!c.mode && last && !last.mode && !last.slabRows && last.title === title) last.items.push({ ...c, hint });
    else cards.push({ title, mode: c.mode, items: [{ ...c, hint }] });
  });
  return cards;
};

const display = (c) => {
  const rupee = c.unit.startsWith("₹");
  const suffix = rupee ? c.unit.slice(1) : c.unit;
  return `${rupee ? "₹" : ""}${c.value}${suffix ? ` ${suffix}` : ""}`;
};

// One value inside a card: read-only text, or an input in edit mode (edited values are highlighted
// and keep the current MV Load value next to them)
const ValueField = ({ item, editable, draft, onChange }) => {
  const edited = draft[item.key] !== undefined;
  const changed = edited && String(draft[item.key]) !== String(item.value);
  const rupee = item.unit.startsWith("₹");
  const suffix = rupee ? item.unit.slice(1) : item.unit;
  return (
    <div className="field">
      <label>{item.hint || "Value"}</label>
      {editable && item.editable ? (
        <div className="val">
          {rupee && <span className="unit">₹</span>}
          <input
            className={changed ? "changed" : ""}
            inputMode="decimal"
            value={edited ? draft[item.key] : item.value}
            onChange={(e) => onChange(item.key, e.target.value.replace(item.integer ? /\D/g : /[^\d.]/g, ""))}
          />
          {suffix && <span className="unit">{suffix}</span>}
          {changed && <small className="was">was {item.value}</small>}
        </div>
      ) : (
        <div className="val readonly">{display(item)}</div>
      )}
    </div>
  );
};

// slab-based charge (e.g. Handling Charge): From | To | Unit | ₹ rate | Min per slab
const SlabCard = ({ card, editable, draft, onChange }) => {
  const box = (label, content) => (
    <div className="slab_box">
      <span className="slab_label">{label}</span>
      <span className="slab_value">{content}</span>
    </div>
  );
  const input = (item) => {
    if (!item) return "—";
    const edited = draft[item.key] !== undefined;
    const changed = edited && String(draft[item.key]) !== String(item.value);
    return editable && item.editable ? (
      <>
        <input
          className={changed ? "changed" : ""}
          inputMode="decimal"
          value={edited ? draft[item.key] : item.value}
          onChange={(e) => onChange(item.key, e.target.value.replace(/[^\d.]/g, ""))}
        />
        {changed && <small className="was">was {item.value}</small>}
      </>
    ) : (
      Number(item.value).toFixed(2)
    );
  };
  return (
    <div className="ob_tile wide slab">
      <div className="top">
        <strong>{card.title}</strong>
        <span className="mode_tag flat">Slab based</span>
      </div>
      <p>Rate and minimum by weight slab</p>
      {card.slabRows.map((row) => (
        <div key={row.slab.id} className="slab_row">
          {box("From", Number(row.slab.from) === 0 ? "---" : row.slab.from)}
          {box("To", row.slab.to)}
          {box("UNIT", row.slab.unit)}
          {box("₹", input(row.amount))}
          {box("Min", input(row.minValue))}
        </div>
      ))}
    </div>
  );
};

// Min Freight: Static = just the minimum; Dynamic = the minimum plus its formula, where a number
// typed into the formula (e.g. the 70 in "Docket Charge % 70") is editable
const MinFreightCard = ({ item: c, editable, draft, onChange }) => {
  const edited = draft[c.key] !== undefined;
  const changed = edited && String(draft[c.key]) !== String(c.value);
  return (
    <div className={`ob_tile ${c.formula ? "wide" : ""}`}>
      <div className="top">
        <strong>{c.name}</strong>
        <span className={`mode_tag ${c.mode}`}>{c.mode === "dynamic" ? "Dynamic" : c.mode === "formula" ? "Formula" : "Static"}</span>
      </div>
      <p>{c.mode === "formula" ? (c.value !== null && c.value !== undefined ? "Calculated from this formula; billed at the minimum when it comes out lower" : "Calculated on each shipment from this formula") : c.formula ? "Billed at the minimum when the formula total is below it" : "Minimum freight per shipment"}</p>
      <div className="ob_minfreight">
        {c.value !== null && c.value !== undefined && (
        <div className="field">
          <label>Minimum</label>
          {editable ? (
            <div className="val">
              <span className="unit">₹</span>
              <input
                className={changed ? "changed" : ""}
                inputMode="decimal"
                value={edited ? draft[c.key] : c.value}
                onChange={(e) => onChange(c.key, e.target.value.replace(/[^\d.]/g, ""))}
              />
              {changed && <small className="was">was {c.value}</small>}
            </div>
          ) : (
            <div className="val readonly">₹{c.value}</div>
          )}
        </div>
        )}
        {c.formula && (
          <div className="field formula">
            <label>Formula</label>
            <div className="formula_line">
              {c.formula.map((part, i) => {
                if (!part.key) return <span key={i} className="tok">{part.text}</span>;
                const pEdited = draft[part.key] !== undefined;
                const pChanged = pEdited && String(draft[part.key]) !== String(part.value);
                return editable ? (
                  <span key={i} className="param">
                    <input
                      className={pChanged ? "changed" : ""}
                      inputMode="decimal"
                      value={pEdited ? draft[part.key] : part.value}
                      onChange={(e) => onChange(part.key, e.target.value.replace(/[^\d.]/g, ""))}
                    />
                    {pChanged && <small className="was">was {part.value}</small>}
                  </span>
                ) : (
                  <span key={i} className="tok num">{part.value}</span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// "Surcharges & Commercial Conditions": one card per charge name, grouped by section. A card that
// has several values (minimum + per kg, slabs...) shows them side by side under one heading.
const ChargesTable = ({ charges, editable, draft = {}, onChange }) => {
  const groups = [...new Set(charges.map((c) => c.group))];
  return (
    <div className="ob_surcharges">
      <h5 className="ob_section_title">
        <MdAutoAwesome /> Surcharges &amp; Commercial Conditions
      </h5>
      {groups.map((group) => (
        <div key={group} className="ob_charge_group">
          <h6>{group}</h6>
          <div className="ob_tiles">
            {toCards(charges.filter((c) => c.group === group)).map((card) =>
              card.slabRows ? (
                <SlabCard key={card.items[0].key} card={card} editable={editable} draft={draft} onChange={onChange} />
              ) : card.mode ? (
                <MinFreightCard key={card.items[0].key} item={card.items[0]} editable={editable} draft={draft} onChange={onChange} />
              ) : (
                <div key={card.items[0].key} className={`ob_tile ${card.items.every((i) => !i.editable) ? "fixed" : ""} ${card.items.length > 2 ? "wide" : ""}`}>
                  <div className="top">
                    <strong>{card.title}</strong>
                    {card.items.every((i) => !i.editable) && <span className="fixed_tag">Fixed</span>}
                  </div>
                  <p>{card.items.every((i) => !i.editable) ? "Statutory — not negotiable" : "As per agreement"}</p>
                  <div className="ob_fields">
                    {card.items.map((item) => (
                      <ValueField key={item.key} item={item} editable={editable} draft={draft} onChange={onChange} />
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChargesTable;
